/**
 * buildEventCalendarMonthSubcolumn — produce a `CalendarMonthSubcolumn`
 * for the calendar-list alias row, reading events across every
 * constituent cal-* source via `AggregatingEventsSource`.
 *
 * Mirrors `build-event-subcolumn.ts` (timeline-column) for the grid
 * (calendar-month) layout: it composes `CalendarMonthActions`
 * (aggregating mode), `makeCalendarMonthSubcolumn`, and
 * `registerCalendarMonthGestures` behind a small host the aggregator
 * (`CalendarListAliasPlugin`) populates. Part of converging the `*.event`
 * renderers onto one strategy — see the
 * calendar-month-multicalendar-events project doc.
 */

import { type Option, RowId, type RowLocalId } from "@tento-lona/sheets";
import type { SheetRow } from "@tento-lona";
import type { CalendarMonthSubcolumn } from "@tento-lona/sheets-ui/sheet/calendar-month-subcolumn.ts";

import { CalendarMonthActions } from "./calendar-month-actions.ts";
import {
  AggregatingEventsSource,
  type CalendarMonthEventsHost,
} from "./events-source.ts";
import { registerCalendarMonthGestures } from "./register-calendar-month-gestures.ts";

/**
 * Everything the builder needs from the aggregator. Extends
 * `CalendarMonthEventsHost` (the SDK month read and the list membership
 * the aggregating source reads through) with the layout context, the actions factory, and a per-row
 * actions cache. The cache lives on the aggregator so gestures register
 * exactly once per row — re-binding reuses the cached actions instead
 * of stacking gestures on the layout's manager.
 */
export interface EventCalendarMonthHost extends CalendarMonthEventsHost {
  /** Layout context (widget, gesture manager, coordinator, delegate). */
  readonly layout: CalendarMonthSubcolumn.Host;
  /** App-layer constructor for `CalendarMonthActions`. */
  readonly makeActions: CalendarMonthActions.Factory;
  /** Per-row actions cache (owned by the aggregator). */
  cachedActions(rowId: RowLocalId): Option<CalendarMonthActions>;
  storeActions(rowId: RowLocalId, actions: CalendarMonthActions): void;
}

export namespace EventCalendarMonthSubcolumn {
  /** Build (or reuse cached) the aggregating calendar-month subcolumn for `row`. */
  export function build(
    row: SheetRow.Joined.Handle,
    host: EventCalendarMonthHost,
  ): CalendarMonthSubcolumn {
    const actions = ensureActions(row, host);
    return makeCalendarMonthSubcolumn(row.id, actions, host.layout);
  }
}

function ensureActions(
  row: SheetRow.Joined.Handle,
  host: EventCalendarMonthHost,
): CalendarMonthActions {
  const cached = host.cachedActions(row.id);
  if (cached) return cached;
  const actions = host.makeActions({
    $calendar: host.layout.$calendar,
    delegate: host.layout.delegate,
    coordinator: host.layout.coordinator,
    system: host.layout.system,
    eventsSource: new AggregatingEventsSource(host),
  });
  host.storeActions(row.id, actions);
  registerCalendarMonthGestures(actions, host.layout);
  return actions;
}

/**
 * Wrap row-scoped `CalendarMonthActions` into the layout's
 * `CalendarMonthSubcolumn` protocol. Moved verbatim from
 * `grid-event.ts` so both the (transitional) per-source renderer and
 * this aggregating builder share one implementation.
 */
export function makeCalendarMonthSubcolumn(
  rowLocalId: RowLocalId,
  actions: CalendarMonthActions,
  host: CalendarMonthSubcolumn.Host,
): CalendarMonthSubcolumn {
  return {
    rowId: RowId.fromLocalId(rowLocalId),
    async provideSpans(monthRange) {
      return actions.loadAndBuildSpansForMonth(monthRange);
    },
    provideSpansNow() {
      // Already-loaded data only — buildAllSpans reads the current
      // facade state synchronously and self-computes its grid range.
      return actions.buildAllSpans();
    },
    async provideCellContent($cell, date, tz, isStillValid) {
      await actions.bindEventCellContent(
        $cell,
        date,
        tz,
        host.gestureManager,
        isStillValid,
      );
    },
    provideCellContentNow($cell, date, tz) {
      // Already-loaded data only — render from the current facade state
      // synchronously. Mirrors `provideSpansNow` for the cell layer.
      actions.buildEventCellContentNow(
        $cell,
        date,
        tz,
        host.gestureManager,
      );
    },
    dismissAll() {
      actions.cancelPendingEvent();
      actions.dismissContextMenu();
    },
  };
}
